import { COUNTRIES, STORE_TYPES } from '@gozoki/api-types'
import { isNumber, parseFrenchFloat } from '@gozoki/tools'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const zStoreForm = z.object({
  checkpointStoreRef: z
    .string()
    .nonempty({ message: 'La référence du magasin est requise.' })
    .max(30, {
      message: 'La référence du magasin doit comporter au maximum 30 caractères.',
    }),
  label: z
    .string()
    .nonempty({ message: 'Le libellé est requis.' })
    .max(30, { message: 'Le libellé doit comporter au maximum 30 caractères.' }),

  // Address
  address: z
    .string()
    .nonempty({ message: "L'adresse est requise." })
    .max(100, { message: "L'adresse doit comporter au maximum 100 caractères." }),
  addressAdditionnal: z
    .string()
    .max(100, { message: "L'adresse additionnelle doit comporter au maximum 100 caractères." })
    .nullable(),
  postalCode: z
    .string()
    .nonempty({ message: 'Le code postal est requis.' })
    .max(20, { message: 'Le code postal doit comporter au maximum 20 caractères.' }),
  city: z
    .string()
    .nonempty({ message: 'La ville est requise.' })
    .max(30, { message: 'La ville doit comporter au maximum 30 caractères.' }),
  storeType: z.enum(STORE_TYPES),
  isOnline: z.boolean(),
  lunaStoreRef: z.string().nullable(),
  bearcodName: z.string().nullable(),
  createShipping: z.boolean().optional(),
  legalEntityId: z.number(),
  country: z.enum(COUNTRIES),
  openingDate: z.string().nonempty({ message: "La date d'ouverture est requise" }),
  quantityFactor: z.preprocess(
    (val) => (typeof val === 'number' ? String(val) : val),
    z.string().refine(isNumber).transform(parseFrenchFloat),
  ),
  growthFactor: z.preprocess(
    (val) => (typeof val === 'number' ? String(val) : val),
    z.string().refine(isNumber).transform(parseFrenchFloat),
  ),
  siret: z
    .string()
    .nullable()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') {
          return true
        } // Allow empty/null
        return /^\d{14}$/.test(val)
      },
      {
        message: 'Le SIRET doit contenir exactement 14 chiffres.',
      },
    ),
})

export type StoreFormInput = z.input<typeof zStoreForm>
export type StoreFormOutput = z.output<typeof zStoreForm>

// Form hook
export const useStoreForm = (defaultValues?: Partial<StoreFormInput>) => {
  const form = useForm({
    // Validation function
    defaultValues: {
      checkpointStoreRef: '',
      label: '',
      address: '',
      addressAdditionnal: null,
      postalCode: '',
      city: '',
      storeType: STORE_TYPES[0], // 'BOUTIQUE'
      isOnline: true,
      lunaStoreRef: null,
      bearcodName: null,
      createShipping: false,
      country: COUNTRIES[0], // 'FRANCE'
      siret: null,
      openingDate: DateTime.now().toISO(),
      quantityFactor: 1,
      growthFactor: 1,
      ...defaultValues,
    },
    resolver: zodResolver(zStoreForm),
  })

  return form
}
